// pages/.../WarehouseEdit.tsx
import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import useWarehouseService from '../../../app/services/hooks/useWarehouseService';
import { WarehouseDataForm } from '../../../app/models/Warehouse';
import WarehouseFormContainer from './WarehouseFormContainer';

interface Props {
  warehouseId: number;
  onSaved?: () => void;
  onCancel?: () => void;
  onError?: () => void;
}

type SelectOpt = { id: number | string; name: string };

const WarehouseEdit = ({ warehouseId, onSaved, onCancel, onError }: Props) => {
  const { showLoading, hideLoading } = useContext(AppContext);
  const { editWarehouse, updateWarehouse, fetchingEditWarehouse, fetchingUpdateWarehouse } = useWarehouseService();

  const [warehouse, setWarehouse] = useState<WarehouseDataForm>({
    warehouse_type: 'COLLECTION_YARD',
    name: '',
    address: '',
    center_id: null,
    used_capacity: 0,
    is_active: true
  });
  const [centers, setCenters] = useState<SelectOpt[]>([]);
  const [warehouseTypes, setWarehouseTypes] = useState<SelectOpt[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  useEffect(() => { edit(); }, []);

  const edit = () => {
    showLoading?.('loading', 'Cargando almacén...');
    editWarehouse(warehouseId, {
      onSuccess: (response: ServiceResponse) => {
        setWarehouse(response.data.warehouse);
        setCenters(response.data.centers || []);
        setWarehouseTypes(response.data.warehouse_types || []);
        hideLoading?.();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
        onError && onError();
      }
    });
  };

  const update = () => {
    const requiredErrors: Record<string, string[]> = {};
    if (warehouse.center_id == null || warehouse.center_id === 0) {
      requiredErrors.center_id = ['El centro es obligatorio.'];
    }
    if (!warehouse.warehouse_type?.trim()) {
      requiredErrors.warehouse_type = ['El tipo de almacén es obligatorio.'];
    }
    if (!warehouse.name?.trim()) {
      requiredErrors.name = ['Este campo no puede estar vacío.'];
    }
    if (Object.keys(requiredErrors).length > 0) {
      setErrorFields(requiredErrors);
      return;
    }

    setErrorFields(undefined);
    showLoading?.('loading', 'Actualizando almacén...');
    updateWarehouse(warehouseId, warehouse, {
      onSuccess: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
        onSaved && onSaved();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoading?.();
        setErrorFields(errs.data);
      }
    });
  };

  return (
    <WarehouseFormContainer
      fetching={fetchingEditWarehouse || fetchingUpdateWarehouse}
      action={update}
      cancel={onCancel ?? (() => undefined)}
      warehouse={warehouse}
      setWarehouse={setWarehouse}
      centers={centers}
      setCenters={setCenters}
      warehouseTypes={warehouseTypes}
      setWarehouseTypes={setWarehouseTypes}
      errorFields={errorFields}
    />
  );
};

export default WarehouseEdit;
